import type { CssGeneratorContext } from '../css-generator.model';

/**
 * Event calendar styling. Layout (grid columns, gaps, sizing) lives in the
 * component templates via Tailwind; this generator owns all colors, typography
 * and themed borders. Status modifier classes (.status-danger /
 * .status-warn / .status-success / .status-contrast /
 * .status-secondary) drive chip, agenda swatch and tag coloring; .is-overdue
 * adds a danger ring on top of the status color.
 */
export const styles = ({ dt }: CssGeneratorContext) => `.event-calendar-grid {
  border-top: 1px solid ${dt('eventCalendar.surface.borderColor')};
  border-left: 1px solid ${dt('eventCalendar.surface.borderColor')};
}

.event-calendar-weekday {
  color: ${dt('eventCalendar.weekday.color')};
  background: ${dt('eventCalendar.weekday.background')};
  font-weight: ${dt('eventCalendar.weekday.fontWeight')};
  font-size: ${dt('eventCalendar.weekday.fontSize')};
  border-right: 1px solid ${dt('eventCalendar.surface.borderColor')};
  border-bottom: 1px solid ${dt('eventCalendar.surface.borderColor')};
}

.event-calendar-weekday.is-weekend {
  color: ${dt('eventCalendar.surface.faintColor')};
}

.event-calendar-week-gutter-header {
  color: ${dt('eventCalendar.surface.faintColor')};
  background: ${dt('eventCalendar.weekday.background')};
  font-weight: ${dt('eventCalendar.weekday.fontWeight')};
  font-size: ${dt('eventCalendar.weekday.fontSize')};
  border-right: 1px solid ${dt('eventCalendar.surface.borderColor')};
  border-bottom: 1px solid ${dt('eventCalendar.surface.borderColor')};
}

.event-calendar-week-gutter {
  border: 0;
  border-right: 1px solid ${dt('eventCalendar.surface.borderColor')};
  border-bottom: 1px solid ${dt('eventCalendar.surface.borderColor')};
  background: ${dt('eventCalendar.weekday.background')};
  color: ${dt('eventCalendar.surface.mutedColor')};
  font-weight: ${dt('eventCalendar.weekday.fontWeight')};
  font-size: ${dt('eventCalendar.weekday.fontSize')};
}

.event-calendar-week-gutter:hover {
  color: ${dt('eventCalendar.more.hoverColor')};
}

.event-calendar-cell {
  border-right: 1px solid ${dt('eventCalendar.surface.borderColor')};
  border-bottom: 1px solid ${dt('eventCalendar.surface.borderColor')};
  background: ${dt('eventCalendar.surface.background')};
}

.event-calendar-cell.is-weekend {
  background: ${dt('eventCalendar.day.weekendBackground')};
}

.event-calendar-cell.is-out {
  background: ${dt('eventCalendar.day.outBackground')};
}

/* Today wins over the weekend tint: equal specificity, so this rule must follow .is-weekend. */
.event-calendar-cell.is-today {
  background: ${dt('eventCalendar.day.todayBackground')};
}

.event-calendar-day-number {
  color: ${dt('eventCalendar.day.color')};
  font-weight: ${dt('eventCalendar.day.fontWeight')};
  font-size: ${dt('eventCalendar.day.fontSize')};
}

.event-calendar-cell.is-out .event-calendar-day-number {
  color: ${dt('eventCalendar.day.outColor')};
}

.event-calendar-cell.is-today .event-calendar-day-number {
  color: ${dt('eventCalendar.day.todayColor')};
}

.event-calendar-chip {
  font-size: ${dt('eventCalendar.chip.fontSize')};
  border-radius: ${dt('eventCalendar.chip.borderRadius')};
  padding: ${dt('eventCalendar.chip.paddingY')} ${dt('eventCalendar.chip.paddingX')};
  border-left: 3px solid ${dt('eventCalendar.chip.contrast.border')};
  background: ${dt('eventCalendar.chip.contrast.background')};
  color: ${dt('eventCalendar.chip.text')};
}

.event-calendar-chip.is-dense {
  padding: ${dt('eventCalendar.chip.densePaddingY')} ${dt('eventCalendar.chip.densePaddingX')};
}

.event-calendar-chip:hover {
  filter: brightness(0.97);
}

.event-calendar-chip.status-danger {
  border-left-color: ${dt('eventCalendar.chip.danger.border')};
  background: ${dt('eventCalendar.chip.danger.background')};
}

.event-calendar-chip.status-warn {
  border-left-color: ${dt('eventCalendar.chip.warn.border')};
  background: ${dt('eventCalendar.chip.warn.background')};
}

.event-calendar-chip.status-success {
  border-left-color: ${dt('eventCalendar.chip.success.border')};
  background: ${dt('eventCalendar.chip.success.background')};
}

.event-calendar-chip.status-contrast {
  border-left-color: ${dt('eventCalendar.chip.contrast.border')};
  background: ${dt('eventCalendar.chip.contrast.background')};
}

.event-calendar-chip.status-secondary {
  border-left-color: ${dt('eventCalendar.chip.secondary.border')};
  background: ${dt('eventCalendar.chip.secondary.background')};
}

.event-calendar-chip.is-overdue {
  box-shadow: inset 0 0 0 1px ${dt('eventCalendar.chip.overdueRing')};
}

.event-calendar-chip .abbreviation {
  font-weight: 700;
}

.event-calendar-chip .name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-calendar-chip .overdue-marker {
  color: ${dt('eventCalendar.chip.overdueMarker')};
}

.event-calendar-more {
  color: ${dt('eventCalendar.more.color')};
  font-weight: ${dt('eventCalendar.more.fontWeight')};
  font-size: ${dt('eventCalendar.more.fontSize')};
}

.event-calendar-more:hover {
  color: ${dt('eventCalendar.more.hoverColor')};
  text-decoration: underline;
}

.event-calendar-popover-header {
  color: ${dt('eventCalendar.weekday.color')};
  font-weight: 700;
  font-size: 0.75rem;
}

/* WEEK */
.event-calendar-week-column {
  border-right: 1px solid ${dt('eventCalendar.week.columnBorderColor')};
  border-bottom: 1px solid ${dt('eventCalendar.week.columnBorderColor')};
}

.event-calendar-week-header {
  background: ${dt('eventCalendar.week.header.background')};
  border-bottom: 1px solid ${dt('eventCalendar.week.columnBorderColor')};
}

.event-calendar-week-header.is-weekend {
  background: ${dt('eventCalendar.day.weekendBackground')};
}

/* Today wins over the weekend tint: equal specificity, so this rule must follow .is-weekend. */
.event-calendar-week-header.is-today {
  background: ${dt('eventCalendar.week.header.todayBackground')};
}

.event-calendar-week-body.is-weekend {
  background: ${dt('eventCalendar.day.weekendBackground')};
}

.event-calendar-week-header .weekday {
  color: ${dt('eventCalendar.week.header.weekdayColor')};
  font-weight: ${dt('eventCalendar.week.header.weekdayFontWeight')};
  font-size: 0.75rem;
}

.event-calendar-week-header .day-number {
  color: ${dt('eventCalendar.week.header.dayColor')};
  font-weight: 700;
  font-size: 1.125rem;
}

/* Mute weekend column labels to the month-view day-number shade; :not(.is-today) lets today's label styling win. */
.event-calendar-week-header.is-weekend:not(.is-today) .weekday {
  color: ${dt('eventCalendar.surface.mutedColor')};
}

.event-calendar-week-header.is-weekend:not(.is-today) .day-number {
  color: ${dt('eventCalendar.surface.mutedColor')};
}

.event-calendar-week-header.is-today .day-number {
  color: ${dt('eventCalendar.week.header.todayColor')};
}

/* AGENDA */
.event-calendar-agenda-day {
  color: ${dt('eventCalendar.agenda.dayHeader.color')};
  background: ${dt('eventCalendar.agenda.dayHeader.background')};
  font-weight: ${dt('eventCalendar.agenda.dayHeader.fontWeight')};
  font-size: ${dt('eventCalendar.agenda.dayHeader.fontSize')};
  border-top: 1px solid ${dt('eventCalendar.agenda.row.borderColor')};
}

.event-calendar-agenda-row {
  border-top: 1px solid ${dt('eventCalendar.agenda.row.borderColor')};
}

.event-calendar-agenda-row:hover {
  background: ${dt('eventCalendar.agenda.row.hoverBackground')};
}

.event-calendar-agenda-swatch {
  border-radius: ${dt('border.radius.sm')};
  background: ${dt('eventCalendar.agenda.swatch.contrast')};
}

.event-calendar-agenda-swatch.status-danger {
  background: ${dt('eventCalendar.agenda.swatch.danger')};
}

.event-calendar-agenda-swatch.status-warn {
  background: ${dt('eventCalendar.agenda.swatch.warn')};
}

.event-calendar-agenda-swatch.status-success {
  background: ${dt('eventCalendar.agenda.swatch.success')};
}

.event-calendar-agenda-swatch.status-contrast {
  background: ${dt('eventCalendar.agenda.swatch.contrast')};
}

.event-calendar-agenda-swatch.status-secondary {
  background: ${dt('eventCalendar.agenda.swatch.secondary')};
}

.event-calendar-agenda-title {
  color: ${dt('eventCalendar.agenda.title.color')};
  font-weight: ${dt('eventCalendar.agenda.title.fontWeight')};
  font-size: ${dt('eventCalendar.agenda.title.fontSize')};
}

.event-calendar-agenda-subtitle {
  color: ${dt('eventCalendar.agenda.subtitle.color')};
  font-size: ${dt('eventCalendar.agenda.subtitle.fontSize')};
}

.event-calendar-agenda-remaining {
  color: ${dt('eventCalendar.agenda.remaining.color')};
  font-weight: ${dt('eventCalendar.agenda.remaining.fontWeight')};
  font-size: ${dt('eventCalendar.agenda.remaining.fontSize')};
}

.event-calendar-agenda-remaining.is-overdue {
  color: ${dt('eventCalendar.agenda.remaining.overdueColor')};
}

.event-calendar-agenda-chevron {
  color: ${dt('eventCalendar.agenda.chevron.color')};
}

/* MINI MONTHS (year view) */
.event-calendar-mini {
  border: 1px solid ${dt('eventCalendar.mini.borderColor')};
  border-radius: ${dt('border.radius.lg')};
  overflow: hidden;
}

.event-calendar-mini-header {
  background: ${dt('eventCalendar.mini.header.background')};
  color: ${dt('eventCalendar.mini.header.color')};
  font-size: ${dt('eventCalendar.mini.header.fontSize')};
  font-weight: ${dt('eventCalendar.mini.header.fontWeight')};
  border-bottom: 1px solid ${dt('eventCalendar.mini.borderColor')};
}

.event-calendar-mini-header:hover {
  color: ${dt('eventCalendar.mini.header.hoverColor')};
}

.event-calendar-mini-weekday {
  color: ${dt('eventCalendar.mini.weekday.color')};
  font-size: ${dt('eventCalendar.mini.weekday.fontSize')};
  font-weight: ${dt('eventCalendar.mini.weekday.fontWeight')};
}

.event-calendar-mini-day {
  color: ${dt('eventCalendar.mini.day.color')};
  font-size: ${dt('eventCalendar.mini.day.fontSize')};
  border-radius: ${dt('border.radius.sm')};
}

.event-calendar-mini-day.is-regular {
  font-size: ${dt('eventCalendar.mini.day.regularFontSize')};
}

.event-calendar-mini-day.is-out {
  color: transparent;
}

.event-calendar-mini-day.is-today {
  outline: 2px solid ${dt('eventCalendar.mini.day.todayOutline')};
  font-weight: 700;
}

/* DENSITY HEATMAP (year view) */
.event-calendar-mini-day.heat-0 {
  background: ${dt('eventCalendar.heat.bucket0')};
}

.event-calendar-mini-day.heat-1 {
  background: ${dt('eventCalendar.heat.bucket1')};
}

.event-calendar-mini-day.heat-2 {
  background: ${dt('eventCalendar.heat.bucket2')};
}

.event-calendar-mini-day.heat-3 {
  background: ${dt('eventCalendar.heat.bucket3')};
  color: ${dt('eventCalendar.heat.bucket3Color')};
}

.event-calendar-mini-day.heat-4 {
  background: ${dt('eventCalendar.heat.bucket4')};
  color: ${dt('eventCalendar.heat.bucket4Color')};
}

.event-calendar-heat-legend {
  color: ${dt('eventCalendar.legend.labelColor')};
  font-size: ${dt('eventCalendar.legend.fontSize')};
}

.event-calendar-heat-swatch {
  border-radius: ${dt('border.radius.sm')};
}

/* EMPTY STATE */
.event-calendar-empty {
  color: ${dt('eventCalendar.empty.color')};
}

.event-calendar-empty .title {
  color: ${dt('eventCalendar.empty.titleColor')};
  font-size: ${dt('eventCalendar.empty.titleFontSize')};
  font-weight: ${dt('eventCalendar.empty.titleFontWeight')};
}

/* TOOLBAR */
.event-calendar-toolbar-title {
  color: ${dt('eventCalendar.toolbar.titleColor')};
  font-size: ${dt('eventCalendar.toolbar.titleFontSize')};
  font-weight: ${dt('eventCalendar.toolbar.titleFontWeight')};
}

/* Separator between toolbar and the filter panel; only rendered while the panel is open. */
.event-calendar-filter-panel {
  border-top: 1px solid ${dt('eventCalendar.toolbar.borderColor')};
}

.event-calendar-view-switch {
  background: ${dt('eventCalendar.viewSwitch.trackBackground')};
  border-radius: ${dt('border.radius.md')};
  padding: 0.1875rem;
}

.event-calendar-view-button {
  border: 0;
  background: transparent;
  border-radius: ${dt('border.radius.sm')};
  padding: 0.4375rem 0.75rem;
  cursor: pointer;
  color: ${dt('eventCalendar.viewSwitch.buttonColor')};
  font-weight: ${dt('eventCalendar.viewSwitch.fontWeight')};
  font-size: ${dt('eventCalendar.viewSwitch.fontSize')};
}

.event-calendar-view-button.is-active {
  background: ${dt('eventCalendar.viewSwitch.activeBackground')};
  color: ${dt('eventCalendar.viewSwitch.activeColor')};
}

/* LEGEND */
.event-calendar-legend {
  color: ${dt('eventCalendar.legend.labelColor')};
  font-size: ${dt('eventCalendar.legend.fontSize')};
}

.event-calendar-legend-label {
  color: ${dt('eventCalendar.legend.labelColor')};
  font-size: ${dt('eventCalendar.legend.fontSize')};
}

.event-calendar-legend-overdue {
  color: ${dt('eventCalendar.legend.overdueColor')};
  font-weight: 600;
}

/* QUICK VIEW */
.event-calendar-quick-view-row {
  border-bottom: 1px dashed ${dt('eventCalendar.quickView.rowBorderColor')};
}

.event-calendar-quick-view-row .key {
  color: ${dt('eventCalendar.quickView.keyColor')};
}

.event-calendar-quick-view-row .value {
  color: ${dt('eventCalendar.quickView.valueColor')};
  font-weight: ${dt('eventCalendar.quickView.valueFontWeight')};
  text-align: right;
}

.event-calendar-quick-view-row .value.is-overdue {
  color: ${dt('eventCalendar.quickView.overdueColor')};
}
`;
